"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import { ChevronDown, GraduationCap, Search, SlidersHorizontal, TrainFront } from "lucide-react";
import { Button } from "@/components/v2/Button";
import { Pill } from "@/components/v2/Pill";
import { statusMeta } from "@/lib/v2/format";
import { describeNear, matchPlaces, nearKey, type NearPlace } from "@/lib/v2/nearPlaces";
import { flattenGrouped, propertyHref, type PropertyLite } from "@/lib/v2/property";
import { cn } from "@/lib/utils";
import {
  BEDS_OPTIONS,
  BUDGET_OPTIONS,
  ChipSelect,
  DistrictChip,
  NearChip,
  STATUS_OPTIONS,
  TENURE_OPTIONS,
  TOP_OPTIONS,
} from "./FilterChips";

export type SearchState = {
  q: string;
  budget: string;
  beds: string;
  districts: string[];
  top: string;
  status: string;
  tenure: string;
  /** Place filter, `mrt:<slug>` or `school:<slug>` (see lib/v2/nearPlaces). */
  near: string;
};

export const EMPTY_SEARCH: SearchState = {
  q: "",
  budget: "",
  beds: "",
  districts: [],
  top: "",
  status: "",
  tenure: "",
  near: "",
};

export function searchStateToQuery(s: SearchState): string {
  const params = new URLSearchParams();
  if (s.q.trim()) params.set("q", s.q.trim());
  if (s.budget) params.set("budget", s.budget);
  if (s.beds) params.set("beds", s.beds);
  if (s.districts.length) params.set("districts", s.districts.join(","));
  if (s.top) params.set("top", s.top);
  if (s.status) params.set("status", s.status);
  if (s.tenure) params.set("tenure", s.tenure);
  if (s.near) params.set("near", s.near);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function SmartSearch({
  variant = "hero",
  initial,
  onChange,
}: {
  variant?: "hero" | "bar";
  initial?: Partial<SearchState>;
  /** bar variant: called on every change so the results page can sync the URL. */
  onChange?: (s: SearchState) => void;
}) {
  const router = useRouter();
  const [state, setState] = useState<SearchState>({ ...EMPTY_SEARCH, ...initial });
  const [suggestions, setSuggestions] = useState<PropertyLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [focused, setFocused] = useState(false);
  // Phones: the six chips filled the whole first screen, so the results
  // page folds them behind one button. Desktop always shows them.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters =
    (state.budget ? 1 : 0) + (state.beds ? 1 : 0) + (state.districts.length ? 1 : 0) + (state.top ? 1 : 0) + (state.status ? 1 : 0) + (state.tenure ? 1 : 0) + (state.near ? 1 : 0);
  const boxRef = useRef<HTMLDivElement>(null);
  const isBar = variant === "bar";

  // Latest state without re-creating `update`; onChange must run outside
  // the setState updater (React forbids parent updates during render).
  const stateRef = useRef(state);
  stateRef.current = state;
  const update = useCallback(
    (patch: Partial<SearchState>) => {
      const next = { ...stateRef.current, ...patch };
      setState(next);
      if (isBar) onChange?.(next);
    },
    [isBar, onChange]
  );

  // Debounced keyword suggestions. The request is only for the typed
  // name; chips do not filter suggestions.
  useEffect(() => {
    const q = state.q.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSearched(false);
      return;
    }
    const api = process.env.NEXT_PUBLIC_API_URL;
    if (!api) return;
    setLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${api}/property?keyword=${encodeURIComponent(q)}`, { signal: controller.signal });
        setSuggestions(flattenGrouped(res.data).slice(0, 8));
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
        setSearched(true);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [state.q]);

  useEffect(() => {
    if (!focused) return;
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setFocused(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [focused]);

  const submit = () => {
    setFocused(false);
    router.push(`/new-launches${searchStateToQuery(state)}`);
  };

  // Picking a station or school replaces the typed text with a place filter.
  const pickPlace = (place: NearPlace) => {
    setFocused(false);
    const patch = { q: "", near: nearKey(place) };
    if (isBar) {
      update(patch);
      return;
    }
    const next = { ...state, ...patch };
    setState(next);
    router.push(`/new-launches${searchStateToQuery(next)}`);
  };

  const places = useMemo(() => matchPlaces(state.q), [state.q]);
  const placeCount = places.mrt.length + places.schools.length;
  const nearLabel = state.near ? describeNear(state.near) : "";
  const typed = focused && state.q.trim().length >= 2;
  // Hero: places above project suggestions. Bar: places only, projects
  // already filter live below.
  const showSuggest = typed && (!isBar || placeCount > 0);
  const chips = useMemo(
    () => (
      <>
        {nearLabel && <NearChip label={nearLabel} onClear={() => update({ near: "" })} />}
        <ChipSelect options={BUDGET_OPTIONS} value={state.budget} onChange={(v) => update({ budget: v })} />
        <ChipSelect options={BEDS_OPTIONS} value={state.beds} onChange={(v) => update({ beds: v })} />
        <DistrictChip value={state.districts} onChange={(v) => update({ districts: v })} />
        <ChipSelect options={TOP_OPTIONS} value={state.top} onChange={(v) => update({ top: v })} />
        {isBar && <ChipSelect options={STATUS_OPTIONS} value={state.status} onChange={(v) => update({ status: v })} />}
        {isBar && <ChipSelect options={TENURE_OPTIONS} value={state.tenure} onChange={(v) => update({ tenure: v })} />}
      </>
    ),
    [state, update, isBar, nearLabel]
  );

  return (
    <div ref={boxRef} className={isBar ? "w-full" : "w-full rounded-2xl bg-white p-3 shadow-card md:p-4"}>
      <div
        className={cn(
          isBar
            ? "grid gap-2 md:grid-cols-[minmax(12rem,1.4fr)_repeat(6,minmax(0,1fr))]"
            : "flex flex-col gap-3",
          isBar && nearLabel && "md:grid-cols-[minmax(12rem,1.4fr)_repeat(7,minmax(0,1fr))]"
        )}
      >
        <div className="relative">
          <label htmlFor={`smart-search-${variant}`} className="sr-only">
            Search a project, district, MRT or school
          </label>
          <Search aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
          <input
            id={`smart-search-${variant}`}
            type="text"
            autoComplete="off"
            value={state.q}
            onChange={(e) => update({ q: e.target.value })}
            onFocus={() => setFocused(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            placeholder={isBar ? "Project, MRT or school" : "Search a project, district, MRT or school"}
            className="w-full rounded-full border border-line bg-white py-3 pl-11 pr-4 text-sm text-ink placeholder:text-ink-faint focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          {showSuggest && (
            <div className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border border-line bg-white shadow-card">
              {placeCount > 0 && (
                <ul className={cn(!isBar && "border-b border-line")}>
                  {[...places.mrt, ...places.schools].map((place) => {
                    const Icon = place.kind === "mrt" ? TrainFront : GraduationCap;
                    return (
                      <li key={nearKey(place)}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickPlace(place)}
                          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface"
                        >
                          <span className="flex min-w-0 items-center gap-3">
                            <Icon aria-hidden="true" className="size-4 shrink-0 text-brand-600" />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-semibold text-ink">
                                {place.kind === "mrt" ? `${place.label} MRT` : place.label}
                              </span>
                              <span className="block text-xs text-ink-muted">{place.kind === "mrt" ? "MRT station" : "School"}</span>
                            </span>
                          </span>
                          <span className="shrink-0 text-xs font-medium text-ink-muted">Launches nearby</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {!isBar && loading && !searched && <p className="px-4 py-3 text-sm text-ink-muted">Searching</p>}
              {!isBar && !loading && searched && suggestions.length === 0 && (
                <p className="px-4 py-3 text-sm text-ink-muted">No projects match that yet</p>
              )}
              {!isBar && suggestions.length > 0 && (
                <ul>
                  {suggestions.map((p) => {
                    const s = statusMeta(p.construction_status || "Upcoming", p.isPreLaunch);
                    return (
                      <li key={p._id}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => router.push(propertyHref(p))}
                          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface"
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-ink">{p.title || p.name}</span>
                            <span className="block text-xs text-ink-muted">{p.district || "Singapore"}</span>
                          </span>
                          <Pill tone={s.tone}>{s.label}</Pill>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
        {isBar ? (
          <>
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              className="flex items-center justify-between rounded-full border border-line bg-white px-4 py-3 text-sm font-semibold text-ink md:hidden"
            >
              <span className="inline-flex items-center gap-2">
                <SlidersHorizontal aria-hidden="true" className="size-4 text-brand-600" />
                Filters{activeFilters > 0 ? ` (${activeFilters})` : ""}
              </span>
              <ChevronDown aria-hidden="true" className={cn("size-4 transition-transform", filtersOpen && "rotate-180")} />
            </button>
            <div className={cn("contents", !filtersOpen && "max-md:hidden")}>{chips}</div>
          </>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
            {chips}
            <Button variant="primary" size="md" onClick={submit} className="w-full lg:w-auto">
              Show projects
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
